import { useMutation } from "@tanstack/react-query";
import {
  getApiUrl,
  getInvoicesDownloadZipPath,
  type SettlementInvoiceType,
} from "Infrastructure/Api/Api";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import FileSaver from "file-saver";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Invoice.BulkDownload;

type Params = {
  type: SettlementInvoiceType;
  settlementPeriodId: number;
  periodLabel?: string;
};

const fetchInvoicesZip = async ({ type, settlementPeriodId }: Params) => {
  const route = `${getApiUrl()}${getInvoicesDownloadZipPath()}?settlementPeriodId=${settlementPeriodId}&type=${type}`;
  const response = await fetch(route, {
    method: "GET",
    mode: "cors",
    headers: { Authorization: getLocalStorageItem(JWT_KEY) ?? "" },
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  return response;
};

export const useDownloadInvoicesZipMutation = () => {
  const { t } = useResource();

  return useMutation({
    mutationFn: async (params: Params) => {
      const response = await fetchInvoicesZip(params);
      const blob = await response.blob();
      const periodPart =
        params.periodLabel ?? String(params.settlementPeriodId);
      const fileName = `${params.type}_invoices_${periodPart}.zip`;

      FileSaver.saveAs(blob, fileName);
    },
    onSuccess: () => {
      toast.success(t(RESOURCES.Success));
    },
    onError: () => {
      toast.error(t(RESOURCES.Error));
    },
  });
};
